// src/components/BuildingDesigner/TypePicker.jsx

import React from "react";
import styled from "styled-components";

const TYPES = [
  { key: "BEDROOM",   label: "Bedroom",     group: "room" },
  { key: "BATHROOM",  label: "Bathroom",    group: "room" },
  { key: "KITCHEN",   label: "Kitchen",     group: "room" },
  { key: "LIVING",    label: "Living",      group: "room" },
  { key: "DINING",    label: "Dining",      group: "room" },
  { key: "HALL",      label: "Hall",        group: "room" },
  { key: "STORAGE",   label: "Storage",     group: "room" },
  { key: "BALCONY",   label: "Balcony",     group: "special" },
  { key: "STAIRS",    label: "Stairs",      group: "special" },
  { key: "SHAFT",     label: "Stair Shaft", group: "special" },
  { key: "CUSTOM",    label: "Custom",      group: "polygon" },
  { key: "BEAM",          label: "Beam",           group: "beam" },
  { key: "CLOSE_OPENING", label: "Close Opening",  group: "beam" },
  { key: "DETECT_ROOM",   label: "🔍 Detect Room", group: "beam" },
  { key: "COLUMN",        label: "Column",         group: "structure" },
];

// Which types are implemented and clickable?
const STAGE2_TYPES = new Set([
  "BEDROOM","BATHROOM","KITCHEN","LIVING","DINING","HALL","STORAGE","BALCONY",
  "CUSTOM",   // Stage 3a — polygon shape (openings in 3b)
  "SHAFT",    // Stage 3c — stair shaft (elevators in 3c-2)
  "STAIRS",   // Stage 3h — outdoor simple stairs
  "BEAM",     // Stage 3j — standalone wall (2 endpoints)
  "CLOSE_OPENING",  // Stage 4b — snap to wall endpoints to close gaps
  "DETECT_ROOM",    // Stage 4e — click inside a room to auto-select its walls
  "COLUMN",   // Stage 5 — structural column (click to place, red rectangle)
]);

const Button = styled.button`
  display: block;
  width: 100%;
  padding: 8px 10px;
  margin-bottom: 6px;
  font-size: 13px;
  text-align: left;
  border: 1px solid ${p => p.$active ? "teal" : "#ddd"};
  background: ${p => p.$active ? "teal" : (p.$disabled ? "#f8f8f8" : "white")};
  color:  ${p => p.$active ? "white" : (p.$disabled ? "#aaa" : "#333")};
  border-radius: 6px;
  cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
  transition: all 0.15s;
  &:hover { border-color: ${p => p.$disabled ? "#ddd" : "teal"}; }
`;

const GroupLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  color: #999;
  margin: 12px 0 4px 0;
  letter-spacing: 0.5px;
`;

// If there's work-in-progress, ask before switching type.
function confirmSwitch(inProgress, targetType) {
  if (!inProgress) return true;
  return window.confirm(
    `You have an unsaved ${inProgress} in progress. Discard it and start a new ${targetType}?`
  );
}

export default function TypePicker({
  selectedType, setSelectedType, inProgressType, onDiscard,
}) {
  const groups = ["room", "special", "polygon", "beam", "structure"];
  const groupLabels = {
    room:    "Rectangular rooms",
    special: "Special elements",
    polygon: "Polygon",
    beam:    "Standalone beam",
    structure: "Structure",
  };

  const handlePick = (key) => {
    if (!STAGE2_TYPES.has(key)) {
      window.alert(`${key} is coming in Stage 3.`);
      return;
    }
    if (selectedType === key) return;
    if (!confirmSwitch(inProgressType, key)) return;
    if (inProgressType) onDiscard();
    setSelectedType(key);
  };

  return (
    <>
      {groups.map(g => (
        <React.Fragment key={g}>
          <GroupLabel>{groupLabels[g]}</GroupLabel>
          {TYPES.filter(t => t.group === g).map(t => {
            const disabled = !STAGE2_TYPES.has(t.key);
            return (
              <Button
                key={t.key}
                $active={selectedType === t.key}
                $disabled={disabled}
                onClick={() => handlePick(t.key)}
                title={disabled ? "Coming in Stage 3" : ""}
              >
                {t.label}{disabled ? "  (soon)" : ""}
              </Button>
            );
          })}
        </React.Fragment>
      ))}
    </>
  );
}
